import { useContext, useEffect, useState } from 'react';
import { Grid, Flexbox, Divider, Accordion, Sizes } from '@sede-enterprise/shell-ds-react-framework';
import 'leaflet/dist/leaflet.css';
import PowerNcfVolumeChart from './AnalyticsHomeCharts/PowerNcfVolumeChart/PowerNcfVolumeChart';
import PowerNCFVolumeMap from './AnalyticsHomeCharts/PowerNcfVolumeMap/PowerNcfVolumeMap';
import TradeNCounterPartyCards from './TradeNCounterPartyCards/TradeNCounterPartyCards';
import DateSideCard from './DateSideCard/DateSideCard';
import { AppContext } from '../../../../Context/AppContext';
import RegionVolumeChart from './AnalyticsHomeCharts/RegionVolumeChart/RegionVolumeChart';
import SingleRegionVolumeChart from './AnalyticsHomeCharts/RegionVolumeChart/SingleRegionVolumeChart';
import NcfReportingHomeHeader from '../NcfReportingUtils/NcfReportingHomeHeader/NcfReportingHomeHeader';
import { AnalyticsFilterSection } from './AnalyticsFilterSection';
import { ASIA_INDIA, ASIA_JAPAN, ASIA_MALAYSIA, ASIA_PHILIPPINES, REGION_NAMES } from '../../../../api/constants';
import PipelineRunMessageOverlay from '../PipelineRunMessage/PipelineRunMessageOverlay';
import { filterRegionsByAsiaRegion, getFilteredRegionsAnalyticsHome } from '../../../Utils/utils';

export const AnalyticsHome = () => {
  const { state: appDataState, dispatch: appDataDispatch } = useContext(AppContext).appData;
  const { state: analyticsState, dispatch: analyticsDispatch } =
    useContext(AppContext).analyticsHome;
  const userRegion = localStorage.getItem('userRegion') ?? appDataState.userRegion;

  // Individual API response empty states
  const [isNcfByCountryResponseEmpty, setIsNcfByCountryResponseEmpty] = useState(false);
  const [isNcfQuarterwiseResponseEmpty, setIsNcfQuarterwiseResponseEmpty] = useState(false);

  // Ensure isAsiaUser is defined before use
  const isAsiaUser =
    localStorage.getItem('isAsiaUser') !== null
      ? localStorage.getItem('isAsiaUser') === 'true'
      : appDataState.userRegionFlags.isAsiaUser;

  // Refactored filteredRegions to handle 'ASIA'
  const regionList = analyticsState.filterOptions?.region;
  const selectedRegion = analyticsState.filteredValues?.region;
  const asiaRegionList: string[] = filterRegionsByAsiaRegion(regionList, isAsiaUser);
  const filteredRegions = getFilteredRegionsAnalyticsHome({ userRegion, asiaRegionList, selectedRegion });

  useEffect(() => {
    appDataDispatch({ type: 'setCurrentPage', payload: '' });
  }, [appDataDispatch]);

  // useEffect to compute isAnyPrimaryApiResponseEmpty based on individual states
  useEffect(() => {
    const isAnyResponseEmpty = isNcfByCountryResponseEmpty ?? isNcfQuarterwiseResponseEmpty;
    analyticsDispatch({
      type: 'setIsAnyPrimaryApiResponseEmpty',
      payload: isAnyResponseEmpty,
    });
  }, [analyticsDispatch, isNcfByCountryResponseEmpty, isNcfQuarterwiseResponseEmpty]);

  // the below useEffect is to set the filterValue region fixed if user is from specific region
  useEffect(() => {
    if (userRegion !== '') {
      let payloadRegion = [userRegion];
      if (isAsiaUser) {
        payloadRegion = analyticsState.filteredValues.region?.length
          ? analyticsState.filteredValues.region
          : asiaRegionList;
      }
      analyticsDispatch({
        type: 'setFilteredValues',
        payload: {
          ...analyticsState.filteredValues,
          region: payloadRegion,
          commodity: appDataState.userRegionFlags.isAustraliaUser
            ? ['NG']
            : analyticsState.filteredValues.commodity,
        },
      });
      analyticsDispatch({
        type: 'setCptyFilteredValues',
        payload: {
          ...analyticsState.cptyFilteredValues,
          region: payloadRegion,
        },
      });
    } else if (!analyticsState.filteredValues.region?.length && regionList?.length) {
      // Initialize region filter for global users on initial load
      analyticsDispatch({
        type: 'setFilteredValues',
        payload: {
          ...analyticsState.filteredValues,
          region: Object.values(REGION_NAMES),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionList]);

  // Create accordion items - only needed if userRegion is not empty
  const regionAccordionItems = [];
  const regionAmericaKey = 'region-americas';

  // Only build accordion items if userRegion is empty
  if (userRegion === '') {
    // Check if we have AMERICAS region
    const americasRegions = filteredRegions?.filter((region) => region === REGION_NAMES.AMERICAS);

    // Add AMERICAS region as the first accordion item
    if (americasRegions?.length > 0) {
      regionAccordionItems.push({
        label: 'Americas Volumes',
        key: regionAmericaKey,
        children: (
          <Flexbox gap='10px' flexWrap='wrap'>
            {americasRegions.map((region) => (
              <RegionVolumeChart region={region} key={region} />
            ))}
          </Flexbox>
        ),
      });
    }

    // Check if we have ASIA regions
    const asiaRegions = filteredRegions?.filter((region: string) =>
      region.startsWith('ASIA-')
    );

    // Add ASIA regions as a single accordion item
    if (asiaRegions?.length > 0) {
      regionAccordionItems.push({
        label: 'Asia Volumes',
        key: 'region-asia',
        children: (
          <Flexbox gap='10px' flexWrap='wrap'>
            {asiaRegions.map((region) => (
              <RegionVolumeChart region={region} key={region} />
            ))}
          </Flexbox>
        ),
      });
    }

    // Skip AMERICAS and ASIA regions as they are already handled
    const excludedRegions = [REGION_NAMES.AMERICAS, ASIA_JAPAN, ASIA_PHILIPPINES, ASIA_INDIA, ASIA_MALAYSIA];

    //Add other regions individually
    const sortedOtherRegions = filteredRegions
      ?.filter((region) => !excludedRegions.includes(region))
      .sort((a, b) => a.localeCompare(b));

    sortedOtherRegions?.forEach((region) => {
      regionAccordionItems.push({
        label: `${region.charAt(0).toUpperCase() + region.slice(1).toLowerCase()} Volumes`,
        key: `region-${region}`,
        children: <RegionVolumeChart region={region} />,
      });
    });
  }

  // Default active key - set to AMERICAS if present, otherwise the first region
  let defaultAccordionKey: string[] = [];
  if (regionAccordionItems.length > 0) {
    if (regionAccordionItems[0].key === regionAmericaKey) {
      defaultAccordionKey = [regionAmericaKey];
    } else {
      defaultAccordionKey = [regionAccordionItems[0].key];
    }
  }

  let contentToRender;

  if (filteredRegions?.length === 1) {
    // Single region case - show SingleRegionVolumeChart
    contentToRender = (
      <>
        {filteredRegions?.map((region) => (
          <SingleRegionVolumeChart region={region} key={region} />
        ))}
      </>
    );
  } else if (appDataState.userRegion === '') {
    // Global view (userRegion is empty) - use Accordion
    contentToRender = (
      <Accordion
        items={regionAccordionItems}
        defaultActiveKey={defaultAccordionKey}
        size={Sizes.Medium}
      />
    );
  } else {
    // Specific region view (userRegion is not empty) - use Flexbox layout
    contentToRender = (
      <Flexbox gap='10px' flexWrap='wrap'>
        {filteredRegions?.map((region) => (
          <RegionVolumeChart region={region} key={region} />
        ))}
      </Flexbox>
    );
  }

  return (
    <div style={{ height: '100%', background: '#F5F5F5' }} data-testid='analytics-home'>
      <Flexbox gap='24px 24px' style={{ marginLeft: '15px', justifyContent: 'start' }}>
        <AnalyticsFilterSection />
        <Grid gap='10px'>
          <Grid.Cell>
            <Flexbox justifyContent='space-between'>
              <NcfReportingHomeHeader
                title='Historic Gas & Power Volume Report'
                description='Chart & Table view for Historic Gas and Power Volume'
              />
              <DateSideCard />
            </Flexbox>
          </Grid.Cell>
          <Divider size='medium' direction='horizontal' />
          <Grid.Cell>
            <TradeNCounterPartyCards regions={filteredRegions} />
          </Grid.Cell>
          <Grid.Cell>
            <div style={{ position: 'relative' }}>
              <Flexbox gap='10px'>
                <PowerNCFVolumeMap setIsNcfByCountryResponseEmpty={setIsNcfByCountryResponseEmpty} />
                <PowerNcfVolumeChart setIsNcfQuarterwiseResponseEmpty={setIsNcfQuarterwiseResponseEmpty} />
              </Flexbox>
              <div style={{ marginTop: '10px' }}>
                {contentToRender}
              </div>
              <PipelineRunMessageOverlay show={analyticsState.isAnyPrimaryApiResponseEmpty} />
            </div>
          </Grid.Cell>
        </Grid>
      </Flexbox>
    </div>
  );
};